import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcProductCertifiedWeightInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import { Utills } from "../../../../../../../shared/app-configs/utilit";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { useParams } from "react-router-dom";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";

interface Option {
  value: string;
  label: string;
}

interface ProductCertifiedWeightProps {
  formRef: React.MutableRefObject<any>;
  productCertifiedWeightInfoData: ITcProductCertifiedWeightInformation[];
  setProductCertifiedWeightInfoData: React.Dispatch<
    React.SetStateAction<ITcProductCertifiedWeightInformation[]>
  >;
  productData: any; //  same structure as in TcproductStandard
  rawMaterialInfoData: any[];
}

const ProductCertifiedWeight: React.FC<ProductCertifiedWeightProps> = ({
  formRef,
  productCertifiedWeightInfoData,
  setProductCertifiedWeightInfoData,
  productData,
  rawMaterialInfoData,
}) => {
  const { notify } = useNotification();
  const { updateField } = useFormActions("certified-products");
  const { setRestrictPopUpSave, setRawMtrlCodes } = useTransactionCertificateStore();
  const { TE_ID } = useParams();

  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [data, setData] = useState<ITcProductCertifiedWeightInformation[]>(
    Array.isArray(productCertifiedWeightInfoData) ? productCertifiedWeightInfoData : []
  );
  const [tcStandardList, setTcStandardList] = useState<string[]>([]);
  const [productCategory, setProductCategory] = useState<Option[]>([]);
  const [rawMaterials, setRawMaterials] = useState<Option[]>([]);
  const [groupedRawMaterials, setGroupedRawMaterials] = useState<
    { label: string; options: Option[] }[]
  >([]);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { executeGet } = useApi();

  // Helper: extract base standard from product claims category
  // e.g. "GRS", "GRS (No Label)", "GRS - Global Recycled Standard" → "GRS"
  const extractProductStandard = (code: string): string => {
    if (!code) return "";
    const normalized = code.trim();
    if (normalized.toLowerCase() === "na") return "na";
    // Common standards
    if (normalized.startsWith("RAS")) return "RAS";
    if (normalized.startsWith("RWS")) return "RWS";
    if (normalized.startsWith("GRS")) return "GRS";
    if (normalized.startsWith("RCS")) return "RCS";
    // Fallback: first token before space or "-"
    const parts = normalized.split(/[\s-]/);
    return parts[0]?.trim().toUpperCase() || normalized.toUpperCase();
  };

  // Validation
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        tcProductClaimsCategory: Yup.string()
          .required("Product Claims Category is required")
          .test(
            "unique-product-claims-standard",
            "Only 1 claims category per standard is allowed for a product",
            function (value) {
              if (!value || editingRowIndex === -1) return true;

              const selectedStandard = extractProductStandard(value);
              const selectedRaw = value.trim();

              // Allow multiple rows with "na" / no real standard
              if (!selectedStandard || selectedStandard.toLowerCase() === "na") {
                return true;
              }

              // Allow same exact option multiple times (e.g., "GRS" with "GRS")
              // but block mixing variants of the same standard (e.g., "GRS" vs "GRS (No Label)")
              const hasVariantClash = data.some((row, index) => {
                if (index === editingRowIndex) return false; // Skip current editing row
                if (!row.tcProductClaimsCategory) return false;
                const rowValue = String(row.tcProductClaimsCategory).trim();
                const rowStandard = extractProductStandard(rowValue);
                return rowStandard === selectedStandard && rowValue !== selectedRaw;
              });

              return !hasVariantClash;
            }
          ),
        tcProductRawMaterialCodeClaimsCategory: Yup.string().required(
          "Product Certified Raw Material is required"
        ),
        tcProductCertifiedWeight: Yup.string()
          .required("Product Certified Weight is required")
          .test(
            "is-positive-number",
            "Weight must be a positive number",
            (value) => {
              const num = parseFloat(value || "0");
              return !isNaN(num) && num >= 0;
            }
          ),
      }),
    [data, editingRowIndex]
  );

  // Certified RM list (v1 endpoint)
  const fetchCertifiedRawMaterials = async () => {
    try {
      setRestrictPopUpSave(true);
      const response = await executeGet(
        TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
      );
      setRestrictPopUpSave(false);
      if (response?.data) {
        const list = response?.data?.response || response.data;
        setCertifiedRawMaterialCodes(Array.isArray(list) ? list : []);
      }
    } catch (error) {
      console.error("Failed to fetch certified raw materials", error);
      setRestrictPopUpSave(false);
    }
  };

  
    const getRawMaterialCodes = async () => {
      try {
        const result: any = await dtrackItService.getRawMaterialCodes(
          String(TE_ID || "")
        );

        const scRmCodes = Array.isArray(result?.data?.["sc-rmcode"])
          ? result.data["sc-rmcode"].map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];

        const rmCodes = Array.isArray(result?.data?.rmcodes)
          ? result.data.rmcodes.map((t: any) => ({
              value: t.code_1,
              label: `${t.description_1} (${t.code_1})`,
            }))
          : [];

        const legacyFlatList =
          !scRmCodes.length && !rmCodes.length && Array.isArray(result?.data)
            ? result.data.map((t: any) => ({
                value: t.code_1,
                label: `${t.description_1} (${t.code_1})`,
              }))
            : [];

        const combined = [...scRmCodes, ...rmCodes, ...legacyFlatList];

        setRawMaterials(combined);
        setGroupedRawMaterials(
          [
            { label: "SC Raw Material Codes", options: scRmCodes },
            { label: "Other Raw Material Codes", options: rmCodes },
          ].filter((group) => group.options.length > 0)
        );
      } catch (err) {
        notify("Failed to fetch raw material codes", "error");
      } 
    };
    
    
    useEffect(() => {
      const initializeData = async () => {
        setIsLoading(true);
        try {
          await Promise.all([
            fetchCertifiedRawMaterials(),
            getRawMaterialCodes()
          ]);
        } finally {
          setIsLoading(false);
        }
      };
      initializeData();
  }, []);

  //  Helper: Check if Raw Material is Certified against TC standards
  const isCertifiedRM = (code: string) => {
    if (!code || !Array.isArray(certifiedRawMaterialCodes) || certifiedRawMaterialCodes.length === 0) {
      return false;
    }

    const mapping = certifiedRawMaterialCodes.find((item: any) => item.material_code === code);
    const rawStandards: string = mapping?.standard || "";
    if (!rawStandards || rawStandards.toUpperCase() === "NA") return false;

    const standards = rawStandards
      .split("/")
      .map((s: string) => s.trim())
      .filter((s: string) => s && s.toUpperCase() !== "NA");

    if (!standards.length || !Array.isArray(tcStandardList) || tcStandardList.length === 0) {
      return false;
    }

    const tcStandardsLower = tcStandardList.map((s) => String(s).toLowerCase());
    const match = standards.some((std) => tcStandardsLower.includes(std.toLowerCase()));
    console.debug("[ProductCertifiedWeight] isCertifiedRM", { code, standards, tcStandardsLower, match });
    return match;
  };

  const certifiedRmCodes = useMemo(
    () =>
      Array.isArray(rawMaterialInfoData)
        ? rawMaterialInfoData
            .filter((rm) => isCertifiedRM(rm.tcProductRawMaterialCode))
            .map((rm) => rm.tcProductRawMaterialCode)
        : [],
    [rawMaterialInfoData, isCertifiedRM]
  );

  useEffect(() => {
    console.debug("[ProductCertifiedWeight] Certified RM mapping", certifiedRawMaterialCodes);
    console.debug("[ProductCertifiedWeight] TC standards", tcStandardList);
    console.debug("[ProductCertifiedWeight] Certified RM codes (filtered)", certifiedRmCodes);
  }, [certifiedRawMaterialCodes, tcStandardList, certifiedRmCodes]);

  //  Compute Total Certified Product Weight
  const totalCertifiedWeight = useMemo(() => {
    if (!productData || !rawMaterialInfoData.length || !certifiedRawMaterialCodes.length || !tcStandardList.length) return 0;
    const netShippingWeight = Number(productData.tcProductNetShippingWeight || productData.tcNetShippingWeight || 0);
    const supplementaryWeight = Number(productData.tcProductSupplementaryWeight || 0);
    const rawMaterialPercentage = rawMaterialInfoData.reduce(
      (acc, item) =>
        acc +
        Number(
          isCertifiedRM(item.tcProductRawMaterialCode)
            ? item.tcProductRawMaterialPercentage
            : 0
        ),
      0
    );
    const certifiedWeight =
      ((netShippingWeight - supplementaryWeight) * rawMaterialPercentage) / 100;
    const result = Number(certifiedWeight.toFixed(2));
    console.debug("[ProductCertifiedWeight] Total certified weight calculation", {
      netShippingWeight,
      supplementaryWeight,
      rawMaterialPercentage,
      certifiedWeight: result
    });
    return result;
  }, [productData, rawMaterialInfoData, isCertifiedRM, certifiedRawMaterialCodes, tcStandardList]);

  //  Compute Certified Weight per Raw Material
  const certifiedWeightsAcrossRawMaterials = useMemo(() => {
    if (!productData || !rawMaterialInfoData.length || !certifiedRawMaterialCodes.length || !tcStandardList.length) return [];
    const netShippingWeight = Number(productData.tcProductNetShippingWeight || productData.tcNetShippingWeight || 0);
    const supplementaryWeight = Number(productData.tcProductSupplementaryWeight || 0);
    const list = rawMaterialInfoData
      .filter(item => isCertifiedRM(item.tcProductRawMaterialCode))
      .map(item => {
        const weight =
          ((netShippingWeight - supplementaryWeight) *
            Number(item.tcProductRawMaterialPercentage)) /
          100;
        return {
          code: item.tcProductRawMaterialCode,
          weight: Number(weight.toFixed(2)),
        };
      });
    console.debug("[ProductCertifiedWeight] Certified weights across RMs", {
      netShippingWeight,
      supplementaryWeight,
      list
    });
    return list;
  }, [productData, rawMaterialInfoData, isCertifiedRM, certifiedRawMaterialCodes, tcStandardList]);

  // Sum of certified weights across raw materials
  const totalCertifiedWeightSum = useMemo(() => {
    return certifiedWeightsAcrossRawMaterials.reduce((acc, item) => acc + (item.weight || 0), 0);
  }, [certifiedWeightsAcrossRawMaterials]);

  const formik = useFormik<ITcProductCertifiedWeightInformation>({
    initialValues: {
      tcProductClaimsCategory: "",
      tcProductRawMaterialCodeClaimsCategory: "",
      tcProductCertifiedWeight: 0,
      tcProductRawMaterialCodeClaimsCategoryDescription:""
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  
  const tableSum = useMemo(() => {
    return data.reduce((sum, item) => sum + Number(item.tcProductCertifiedWeight || 0), 0);
  }, [data]);


  // Form ref
  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formik,
        editingRowIndex,
        hasUnsavedRow: editingRowIndex !== -1,
        tableSum,
        expectedTotal: totalCertifiedWeightSum,
      };
    }
  }, [formRef, formik, editingRowIndex, tableSum, totalCertifiedWeightSum]);

  // Sync prop changes
  useEffect(() => {
    setData(Array.isArray(productCertifiedWeightInfoData) ? productCertifiedWeightInfoData : []);
  }, [productCertifiedWeightInfoData]);

  useEffect(() => {
    const getBaseDetails = async() => {
      try{
        const res: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS_V4, {
          tcId: TE_ID,
        });
        const standards = res?.data?.tcStandard
        console.log("standards", standards)
        setTcStandardList(standards);
      }catch (err) {
        Utills.cl(`Error fetching Base Details: ${err}`);
        notify("Failed to fetch Base Details", "error");
      }
    }
    getBaseDetails()
  },[])

  useEffect(() => {
    const getProductClaimsCategory = async () => {
      try {
        const response = await executeGet(
          TCAppConfig.GET_SC_PRODUCT_CLAIMS_CATEGORY_V4,
          { tcId: TE_ID }
        );
        const apiData = response?.data || response;
        const productCategoryDetails =
          apiData?.scProductClaimsCategoryDetails || [];

        const categorySet = new Set<string>();
        productCategoryDetails.forEach((detail: any) => {
          (detail?.scProductClaimsCategory || []).forEach((category: string) => {
            if (category) {
              categorySet.add(category);
            }
          });
        });
    
        const categories = Array.from(categorySet);
    
        const filteredCategories =
          tcStandardList.length === 0
            ? categories
            : categories.filter((category) =>
            tcStandardList.some((std: string) =>
                  category.toLowerCase().includes(std.toLowerCase())
            )
          );
    
        const options = filteredCategories.map((cat: string) => ({
          value: cat,
          label: cat,
          }));
    
          setProductCategory(options);
          setRawMtrlCodes(options);
      } catch (err) {
        Utills.cl(`Error fetching product claims category: ${err}`);
        notify("Failed to fetch product claims category", "error");
      }
    };        
  
    if (TE_ID && tcStandardList.length > 0) {
      getProductClaimsCategory();
    }
  }, [tcStandardList, TE_ID]);

  // Table handlers
  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {
    formik.setFieldValue(columnId, value);
    setData(prev => prev.map((row, i) => (i === rowIndex ? { ...row, [columnId]: value } : row)));
  };

  const handleAddNewRow = () => {
    const newRow: ITcProductCertifiedWeightInformation = {
      tcProductClaimsCategory: "",
      tcProductRawMaterialCodeClaimsCategory: "",
      tcProductCertifiedWeight: 0,
      tcProductRawMaterialCodeClaimsCategoryDescription:""
    };
    const newData = [...data, newRow];
    setData(newData);
    setProductCertifiedWeightInfoData(newData);
    setEditingRowIndex(newData.length - 1);
    setRestrictPopUpSave(true);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = () => {
    const newData = editingRowIndex === data.length - 1 ? data.slice(0, -1) : [...data];
    setData(newData);
    setProductCertifiedWeightInfoData(newData);
    try {
      updateField("productCertifiedWeightInfo", newData); // Update global store
    } catch (error) {
      notify("Failed to update data on cancel", "error");
      console.error("Error updating data on cancel:", error);
    }
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Reset unsaved state
  };

  const handleSaveRow = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach(err => notify(err as string, "error"));
      return;
    }
  
    const updatedRow = formik.values;
    const newData = data.map((row, i) => (i === editingRowIndex ? updatedRow : row));
    setData(newData);
    setProductCertifiedWeightInfoData(newData);
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();
    (formik as any).hasUnsavedRow = false; // Explicitly reset unsaved state
  
    try {
      updateField("productCertifiedWeightInfo", newData);
      notify("Row saved successfully", "success");
    } catch (error) {
      notify("Failed to save row", "error");
      console.error("Error saving row:", error);
    }
  };

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex] || formik.initialValues);
    setRestrictPopUpSave(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const newData = data.filter((_, i) => i !== rowIndex);
    setData(newData);
    setProductCertifiedWeightInfoData(newData);
    try {
      updateField("productCertifiedWeightInfo", newData);
      notify("Row deleted successfully", "success");
    } catch (error) {
      notify("Failed to delete row", "error");
      console.error("Error deleting row:", error);
    }
  };

  // Table columns
  const columns: EditableColumnDef<ITcProductCertifiedWeightInformation>[] = [
    {
      accessorKey: "tcProductClaimsCategory",
      header: "Product Claims Category",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Claims Category
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          value={value}
          options={productCategory}
          enableSearch={true}
          placeholder="Select Category"
          onChange={e => {
            const selectedValue = e.target.value;
            const selectedOption = productCategory.find((opt: Option) => opt.value === selectedValue);
            const description = selectedOption?.label ?? "";
      
            // Update Formik values directly
            onChange(selectedValue);
            formik.setFieldValue("tcProductClaimsCategory", selectedValue);
            formik.setFieldValue("tcProductClaimsCategoryDescription", description);
          }}
        />
      ),
    },
    {
      accessorKey: "tcProductRawMaterialCodeClaimsCategory",
      header: "Product Certified Raw Material",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Certified Raw Material
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomSelect
          value={value}
          options={rawMaterials}
          groupedOptions={
            groupedRawMaterials.length > 0 ? groupedRawMaterials : undefined
          }
          enableSearch={true}
          placeholder="Select Raw Material"
          onChange={e => {
            const selectedCode = e.target.value;
            const selectedMaterial = rawMaterials.find(
              (mat) => mat.value === selectedCode
            );
    
            const description : any = selectedMaterial
              ? selectedMaterial.label.split(" (")[0]
              : "";
      
            // Update Formik values directly
            onChange(selectedCode);
            formik.setFieldValue("tcProductRawMaterialCodeClaimsCategory", selectedCode);
            formik.setFieldValue("tcProductRawMaterialCodeClaimsCategoryDescription", description);
          }}
        />
      ),      
    },
    {
      accessorKey: "tcProductCertifiedWeight",
      header: "Product Certified Weight",
      Header: (
        <span>
          <i style={{ color: "red" }}>*</i> Product Certified Weight
        </span>
      ),
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          value={value ?? ""}
          type="number"
          inputProps={{ min: 0 }}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter Weight"
          sx={{ mb: 2 }}
        />
      ),
    },
  ];

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
    >
      {isLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {certifiedRmCodes.length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              No certified raw materials match the TC standards for this product.
            </Typography>
          )}
          <Box mb={3}>
            <Box mt={1}>
              <Typography>
                <span style={{ fontWeight: 600 }}>Total Certified Product Weight: </span>
                {totalCertifiedWeight.toFixed(2)} kg
              </Typography>
            </Box>
            <Box mt={1}>
              <Typography fontWeight="600">Certified Weight Across Raw Materials:</Typography>
              {certifiedWeightsAcrossRawMaterials.length > 0 ? (
                certifiedWeightsAcrossRawMaterials.map((item, i) => (
                  <Typography key={i} ml={2}>
                    {item.code} — {item.weight} kg
                  </Typography>
                ))
              ) : (
                <Typography ml={2} color="text.secondary">
                  No certified raw materials found
                </Typography>
              )}
            </Box>
          </Box>

          <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
            <StyledButton
              disabled={editingRowIndex !== -1}
              onClick={handleAddNewRow}
              sx={{ mb: 2 }}
            >
              Add Product Certified Weight Info
            </StyledButton>
          </Box>

          <EditableTable
            data={data.filter(row => row && Object.keys(row).length > 0)}
            columns={columns}
            onSaveRow={handleSaveRow}
            onCellChange={handleCellChange}
            editingRowIndex={editingRowIndex}
            onEditRow={handleEditRow}
            onCancelEdit={handleCancelEdit}
            onDeleteRow={handleDeleteRow}
          />
        </>
      )}
    </Box>
  );
};

export default ProductCertifiedWeight;
